<template>
  <section class="why-eduversity-section">
    <div class="why-eduversity-wrapper">

      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-80px' }"
        :transition="{ duration: 0.7, ease: 'easeOut' }"
      >
        <div class="section-header">
          <h2 class="section-title">Why <span class="gradient-text">Eduversity</span></h2>
          <p class="section-subtitle">Where University Education Meets Industry Readiness</p>
        </div>
      </Motion>

      <Motion
        :initial="{ opacity: 0, y: 50 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-100px' }"
        :transition="{ duration: 0.8, ease: 'easeOut' }"
      >
        <!-- Gradient-border wrapper -->
        <div class="card-outer">
          <div class="unified-card">

          <!-- Top accent bar spanning all columns -->
          <div class="card-top-bar" />

          <!-- ── Column 1: Partner Universities ───────────── -->
          <div class="pillar university-pillar">
            <div class="pillar-header">
              <img src="/icons/university-ico.svg" alt="University" class="pillar-icon-img" />
              <h3 class="pillar-title">Partner Universities</h3>
            </div>

            <ul class="feature-list">
              <li v-for="(item, i) in universityFeatures" :key="i" class="feature-item"
                :class="{ 'is-open': openUniversity === i }" @mouseenter="openUniversity = i"
                @mouseleave="openUniversity = null" @click="openUniversity = openUniversity === i ? null : i">
                <div class="feature-row">
                  <span class="feature-icon-wrap" v-html="item.icon" />
                  <span class="feature-text">{{ item.title }}</span>
                  <span class="accordion-arrow">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                      stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
                <div class="feature-desc">{{ item.desc }}</div>
              </li>
            </ul>
          </div>

          <!-- ── Operator: + ─────────────────────────────── -->
          <div class="col-operator">
            <div class="operator-badge">
              <span class="operator-sign">+</span>
            </div>
          </div>

          <!-- ── Column 2: Eduversity Ecosystem ───────────── -->
          <div class="pillar eduversity-pillar">
            <div class="pillar-header">
              <img src="/icons/startup-ico.svg" alt="Eduversity" class="pillar-icon-img" />
              <h3 class="pillar-title">Eduversity Ecosystem</h3>
            </div>

            <ul class="feature-list">
              <li v-for="(item, i) in eduversityFeatures" :key="i" class="feature-item"
                :class="{ 'is-open': openEduversity === i }" @mouseenter="openEduversity = i"
                @mouseleave="openEduversity = null" @click="openEduversity = openEduversity === i ? null : i">
                <div class="feature-row">
                  <span class="feature-icon-wrap" v-html="item.icon" />
                  <span class="feature-text">{{ item.title }}</span>
                  <span class="accordion-arrow">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                      stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
                <div class="feature-desc">{{ item.desc }}</div>
              </li>
            </ul>

            <!-- Tech stack chips -->
            <div class="tech-stack">
              <p class="tech-stack-label">Skills You'll Master</p>
              <div class="tech-chips">
                <span v-for="tech in techStack" :key="tech" class="tech-chip">{{ tech }}</span>
              </div>
            </div>
          </div>

          <!-- ── Operator: = ─────────────────────────────── -->
          <div class="col-operator">
            <div class="operator-badge">
              <span class="operator-sign">=</span>
            </div>
          </div>

          <!-- ── Column 3: Result ────────────────────────── -->
          <div class="pillar result-pillar">

            <div class="result-illustration">
              <div class="result-glow" />
              <img src="/svgs/career-progress.svg" alt="Successful Career" class="result-svg" />
            </div>

            <h3 class="result-title">
              Successful Career<br />
              <span class="result-highlight">&amp; Dream Job</span>
            </h3>

            <!-- Stats -->
            <div class="stat-grid">
              <div class="stat">
                <span class="stat-value">500+</span>
                <span class="stat-label">Hiring Partners</span>
              </div>
              <div class="stat">
                <span class="stat-value">92%</span>
                <span class="stat-label">Placement Rate</span>
              </div>
              <div class="stat">
                <span class="stat-value">₹4.2L</span>
                <span class="stat-label">Avg Package</span>
              </div>
            </div>

            <!-- Testimonial -->
            <div class="testimonial">
              <p class="testimonial-quote">"Got placed at TCS in 6 months. The dual learning approach was a complete
                game changer for me."</p>
              <div class="testimonial-author">
                <span class="author-name">Ravi Kumar</span>
                <span class="author-sep">·</span>
                <span class="author-role">SDE @ TCS</span>
              </div>
            </div>

            <!-- CTA -->
            <a href="#" class="result-cta">
              See Placement Stories
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>

        </div>
        </div>
      </Motion>
    </div>
  </section>
</template>

<script setup lang="ts">
const openUniversity = ref<number | null>(null)
const openEduversity = ref<number | null>(null)

const techStack = ['Java', 'Python', 'React', 'SQL', 'AWS', 'DSA', 'Linux', 'Git']

// Monochrome inline SVG icons
const iconGraduationCap = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>`
const iconBook = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>`
const iconBuilding = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/></svg>`
const iconUsers = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`
const iconCalendar = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`
const iconCode = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`
const iconLayers = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>`
const iconAward = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>`
const iconBriefcase = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>`
const iconTarget = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>`

const universityFeatures = [
  {
    icon: iconGraduationCap,
    title: 'Accredited Degree Programs',
    desc: 'Earn globally recognized degrees from UGC-approved universities, ensuring your qualification holds value with every employer.'
  },
  {
    icon: iconBook,
    title: 'Academic Excellence',
    desc: 'Rigorous academic curriculum crafted by seasoned educators, designed to build strong theoretical foundations across disciplines.'
  },
  {
    icon: iconBuilding,
    title: 'Campus Learning',
    desc: 'Immersive on-campus experience with labs, libraries, and peer collaboration that shape holistic personal development.'
  },
  {
    icon: iconUsers,
    title: 'University Faculty',
    desc: 'Learn from experienced professors and domain experts who bring decades of teaching and research expertise to every session.'
  },
  {
    icon: iconCalendar,
    title: 'Semester-Based Curriculum',
    desc: 'Structured learning path with well-defined milestones, exams, and assessments to keep your academic growth on track.'
  }
]

const eduversityFeatures = [
  {
    icon: iconCode,
    title: 'Industry-Aligned Skills',
    desc: 'Master in-demand technologies and frameworks taught by active industry practitioners with real-world engineering experience.'
  },
  {
    icon: iconLayers,
    title: 'Live Projects',
    desc: 'Work on production-grade projects solving actual business problems, building a portfolio that speaks louder than marks.'
  },
  {
    icon: iconAward,
    title: 'Certifications',
    desc: 'Earn industry-recognized certifications from top tech companies and platforms, validating your skills directly to employers.'
  },
  {
    icon: iconBriefcase,
    title: 'Internship Exposure',
    desc: 'Get placed in real companies during your studies, gaining hands-on corporate experience before you even graduate.'
  },
  {
    icon: iconTarget,
    title: 'Placement Support',
    desc: 'Dedicated career team with mock interviews, resume building, and direct referrals to 500+ active hiring partners.'
  }
]
</script>

<style scoped>
/* ── Section ─────────────────────────────────── */
.why-eduversity-section {
  padding: 6rem 2rem;
  position: relative;
  z-index: 10;
  overflow: hidden;
}

.why-eduversity-wrapper {
  max-width: 1440px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

/* ── Section header ──────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 2rem;
}

.section-title {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
}

.section-subtitle {
  font-size: 1.25rem;
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
}

.gradient-text {
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── Gradient border wrapper ─────────────────── */
.card-outer {
  padding: 1px;
  background: linear-gradient(90deg,
      rgba(59, 130, 246, 0.55) 0%,
      rgba(255, 120, 0, 0.55) 50%,
      rgba(255, 187, 63, 0.55) 100%);
  border-radius: 28px;
}

/* ── Unified card ────────────────────────────── */
.unified-card {
  display: grid;
  grid-template-columns: 1fr 52px 1fr 52px 360px;
  grid-template-rows: 3px 1fr;
  background: var(--bg-secondary);
  border-radius: 27px;
  overflow: hidden;
  box-shadow: var(--shadow-card);
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

/* ── Top accent bar ──────────────────────────── */
.card-top-bar {
  grid-column: 1 / -1;
  grid-row: 1;
  background: linear-gradient(90deg, #3b82f6 0%, #ff7800 50%, #ffbb3f 100%);
}

/* ── Operator columns (+, =) ─────────────────── */
.col-operator {
  grid-row: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid var(--border-color);
  border-right: 1px solid var(--border-color);
  transition: border-color 0.3s ease;
}

.operator-badge {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 120, 0, 0.45);
  background: rgba(255, 120, 0, 0.07);
  box-shadow:
    0 0 18px rgba(255, 120, 0, 0.12),
    inset 0 0 12px rgba(255, 120, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

/* outer glow ring */
.operator-badge::after {
  content: '';
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  border: 1px solid rgba(255, 120, 0, 0.15);
  pointer-events: none;
}

.operator-sign {
  font-size: 1.1rem;
  font-weight: 900;
  line-height: 1;
  background: linear-gradient(160deg, #ff7800, #ffbb3f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── Pillars ─────────────────────────────────── */
.pillar {
  grid-row: 2;
  padding: 2.75rem 2.25rem;
  display: flex;
  flex-direction: column;
  transition: background 0.35s ease;
}

.university-pillar {
  background: linear-gradient(160deg, rgba(59, 130, 246, 0.04) 0%, transparent 55%);
  position: relative;
}

.university-pillar::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: 0;
  width: 70%;
  height: 70%;
  background-image: url('/svgs/graduation.svg');
  background-repeat: no-repeat;
  background-position: bottom right;
  background-size: contain;
  opacity: 0.8;
  pointer-events: none;
  z-index: 0;
}

/* Keep pillar content above the watermark */
.university-pillar .pillar-header,
.university-pillar .feature-list {
  position: relative;
  z-index: 1;
}

.eduversity-pillar {
  background: linear-gradient(160deg, rgba(255, 120, 0, 0.04) 0%, transparent 55%);
  position: relative;
}

.eduversity-pillar::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: 0;
  width: 25%;
  height: 25%;
  background-image: url('/svgs/certificate.svg');
  background-repeat: no-repeat;
  background-position: bottom right;
  background-size: contain;
  opacity: 0.9;
  pointer-events: none;
  z-index: 0;
  margin-right: 1rem;
}

.eduversity-pillar .pillar-header,
.eduversity-pillar .feature-list,
.eduversity-pillar .tech-stack {
  position: relative;
  z-index: 1;
}

/* Column hover: deepen ambient tint when an item is active */
.university-pillar:has(.feature-item:hover),
.university-pillar:has(.feature-item.is-open) {
  background: linear-gradient(160deg, rgba(59, 130, 246, 0.09) 0%, transparent 60%);
}

.eduversity-pillar:has(.feature-item:hover),
.eduversity-pillar:has(.feature-item.is-open) {
  background: linear-gradient(160deg, rgba(255, 120, 0, 0.09) 0%, transparent 60%);
}

/* ── Pillar header ───────────────────────────── */
.pillar-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.75rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  transition: border-color 0.3s ease;
}

.pillar-icon-img {
  width: 46px;
  height: 46px;
  object-fit: contain;
  flex-shrink: 0;
}

.pillar-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.25;
}

/* ── Feature accordion list ──────────────────── */
.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.feature-item {
  border-radius: 11px;
  background: var(--feature-bg);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.28s ease, border-color 0.28s ease;
  overflow: hidden;
  user-select: none;
}

.feature-item:hover,
.feature-item.is-open {
  background: var(--feature-hover-bg);
  border-color: var(--feature-hover-border);
}

.feature-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.8rem 0.95rem;
}

.feature-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--text-muted);
  transition: color 0.28s ease;
}

.feature-item:hover .feature-icon-wrap,
.feature-item.is-open .feature-icon-wrap {
  color: var(--text-primary);
}

.feature-text {
  font-size: 0.94rem;
  font-weight: 600;
  color: var(--text-secondary);
  flex: 1;
  line-height: 1.3;
  transition: color 0.28s ease;
}

.feature-item.is-open .feature-text {
  color: var(--text-primary);
}

.accordion-arrow {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--text-muted);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), color 0.28s ease;
}

.feature-item.is-open .accordion-arrow {
  transform: rotate(180deg);
  color: var(--text-secondary);
}

.feature-desc {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  font-size: 0.845rem;
  color: var(--text-secondary);
  line-height: 1.65;
  padding: 0 0.95rem 0 2.7rem;
  transition:
    max-height 0.38s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease,
    padding-bottom 0.3s ease;
}

.feature-item.is-open .feature-desc {
  max-height: 8rem;
  opacity: 1;
  padding-bottom: 0.8rem;
}

/* ── Tech stack chips ────────────────────────── */
.tech-stack {
  margin-top: auto;
  padding-top: 1.5rem;
}

.tech-stack-label {
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin: 0 0 0.65rem;
}

.tech-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  width: 90%;
}

.tech-chip {
  padding: 0.28rem 0.65rem;
  background: rgba(255, 120, 0, 0.08);
  border: 1px solid rgba(255, 120, 0, 0.22);
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 187, 63, 0.82);
  letter-spacing: 0.01em;
}

[data-theme="light"] .tech-chip {
  color: #c46a00;
}

/* ── Result pillar ───────────────────────────── */
.result-pillar {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.75rem 1.75rem;
  background: linear-gradient(160deg, rgba(255, 187, 63, 0.06) 0%, rgba(255, 120, 0, 0.03) 100%);
  justify-content: space-between;
}

.result-eyebrow {
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 187, 63, 0.65);
  margin: 0 0 1.25rem;
  align-self: flex-start;
}

/* ── Illustration + animated glow ────────────── */
.result-illustration {
  position: relative;
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}

@keyframes glowPulse {

  0%,
  100% {
    opacity: 0.18;
    transform: scale(1);
  }

  50% {
    opacity: 0.38;
    transform: scale(1.12);
  }
}

.result-glow {
  position: absolute;
  inset: -28px;
  background: radial-gradient(circle, rgba(255, 187, 63, 0.28) 0%, transparent 68%);
  border-radius: 50%;
  pointer-events: none;
  animation: glowPulse 2.6s ease-in-out infinite;
}

.result-svg {
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: relative;
  z-index: 1;
}

.result-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 1.25rem;
  line-height: 1.35;
}

.result-highlight {
  background: linear-gradient(135deg, #ff7800, #ffbb3f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── Stats grid ──────────────────────────────── */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.45rem;
  width: 100%;
  margin-bottom: 1.25rem;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.7rem 0.35rem;
  background: var(--feature-bg);
  border: 1px solid var(--border-color);
  border-radius: 11px;
  gap: 0.2rem;
  transition: background 0.3s ease, border-color 0.3s ease;
}

.stat-value {
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1;
  background: linear-gradient(135deg, #ff7800, #ffbb3f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stat-label {
  font-size: 0.6rem;
  color: var(--text-muted);
  font-weight: 500;
  text-align: center;
  line-height: 1.3;
}

/* ── Testimonial ─────────────────────────────── */
.testimonial {
  width: 100%;
  padding: 0.9rem 1rem;
  background: var(--feature-bg);
  border: 1px solid var(--border-color);
  border-left: 3px solid rgba(255, 187, 63, 0.5);
  border-radius: 0 10px 10px 0;
  text-align: left;
  margin-bottom: 1.1rem;
  transition: background 0.3s ease, border-color 0.3s ease;
}

.testimonial-quote {
  font-size: 0.78rem;
  color: var(--text-secondary);
  line-height: 1.6;
  font-style: italic;
  margin: 0 0 0.5rem;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7rem;
}

.author-name {
  font-weight: 700;
  color: var(--text-secondary);
}

.author-sep {
  color: var(--text-muted);
}

.author-role {
  color: var(--text-muted);
}

/* ── Result CTA ──────────────────────────────── */
.result-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: rgba(255, 187, 63, 0.8);
  font-size: 0.8rem;
  font-weight: 700;
  text-decoration: none;
  letter-spacing: 0.02em;
  transition: color 0.25s ease, gap 0.25s ease;
  align-self: flex-start;
}

.result-cta:hover {
  color: #ffbb3f;
  gap: 0.7rem;
}

/* ── Responsive ──────────────────────────────── */
@media (max-width: 1200px) {
  .unified-card {
    grid-template-columns: 1fr 70px 1fr 70px 260px;
  }

  .pillar {
    padding: 2.25rem 1.75rem;
  }

  .result-illustration {
    width: 150px;
    height: 150px;
  }
}

@media (max-width: 960px) {
  .unified-card {
    grid-template-columns: 1fr;
    grid-template-rows: 3px auto;
  }

  .card-top-bar {
    grid-column: 1;
  }

  .col-operator {
    border-left: none;
    border-right: none;
    border-top: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);
    padding: 1.1rem 0;
    grid-row: auto;
  }

  .pillar {
    grid-row: auto;
    border-left: none;
    border-top: 3px solid transparent;
  }

  .university-pillar {
    border-top-color: rgba(59, 130, 246, 0.45);
  }

  .eduversity-pillar {
    border-top-color: rgba(255, 120, 0, 0.45);
  }

  .result-pillar {
    justify-content: flex-start;
    gap: 1.25rem;
  }

  .tech-stack {
    margin-top: 1.5rem;
  }

  .stat-grid {
    max-width: 340px;
  }
}

@media (max-width: 600px) {
  .why-eduversity-section {
    padding: 4rem 1rem;
  }

  .pillar {
    padding: 2rem 1.25rem;
  }

  .pillar-title {
    font-size: 1.15rem;
  }

  .feature-text {
    font-size: 0.88rem;
  }

  .result-illustration {
    width: 130px;
    height: 130px;
  }

  .result-title {
    font-size: 1.15rem;
  }
}
</style>
